import { useEffect, useMemo } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import { Capacitor } from '@capacitor/core';
import { addBreadcrumb, captureException } from '@sentry/react';

import {
  CurrentCustomerQuery,
  MEMBERSHIP_STATE,
  useCurrentCustomerQuery,
  useCustomerMakeActiveMutation,
} from '@ocx/graphql';
import { logInDev } from '@ocx/utils';
/* eslint-disable @nx/enforce-module-boundaries */
import { useInMemoryStorage } from '@ocx-app/lib/in-memory-storage/use-in-memory-storage';
import { IM_MEMORY_PIN_CODE_STORAGE_KEY } from '@ocx-app/modules/wallet/wallet.constants';
import { useConfigurationContext } from '@ocx-app/modules/configuration/useConfigurationContext';
import { useLogoutController } from '@ocx-app/modules/auth/useLogoutController';
import { useRudderStack } from '@ocx-app/lib/rudderStack/useRudderStack';
import { useAppsFlyer } from '@ocx/cap-appsflyer';
/* eslint-enable @nx/enforce-module-boundaries */

import { Membership } from './membership.types';
import { sentrySetUser } from './sentry.utils';
import { getMembershipFromCurrentCustomer } from './membership.dto';
import { getSanitizedCustomer } from './current-customer.dto';

const handleCurrentCustomerQueryError = (error: unknown) => {
  addBreadcrumb({ message: '[useGetMembershipQuery] Membership data fetching error' });
  captureException(error, {
    tags: {
      source: 'useGetMembershipQuery',
    },
  });
};

export type UseGetMembershipQueryReturns = {
  loading: boolean;
  membership: Membership | null;
  refetch(): Promise<ApolloQueryResult<CurrentCustomerQuery>>;
};

export const useGetMembershipQuery = (): UseGetMembershipQueryReturns => {
  const { isLoggedIn, logout } = useLogoutController();
  const { identify } = useRudderStack();
  const { setCustomerUserId, logEvent: logAppsFlyerEvent } = useAppsFlyer();

  const {
    data,
    loading: isMembershipLoading,
    refetch,
  } = useCurrentCustomerQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      isLoggedIn,
    },
    onError: handleCurrentCustomerQueryError,
  });

  const membership = useMemo(() => {
    if (!data?.customer || !isLoggedIn) {
      return null;
    }
    const customer = getSanitizedCustomer(data);
    if (!customer) {
      return null;
    }
    return getMembershipFromCurrentCustomer(customer);
    // app is really interested only in customer data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.customer, isLoggedIn]);

  // - Each effect here is responsible for own small responsibility -
  // Suspended Member Fraud Prevention
  useEffect(() => {
    if (membership?.state === MEMBERSHIP_STATE.Suspended) {
      logout().catch(() => {
        // I know it's suboptimal, but the code requires proper refactoring to handle it correctly
        logout({
          skipMutation: true,
        }).catch(logInDev);
      });
    }
  }, [logout, membership?.state]);

  const { config, setMembershipOverrides } = useConfigurationContext();
  useEffect(() => {
    // Overrides global configuration by values from membership
    setMembershipOverrides(membership?.customData?.configOverrides || null);
  }, [membership?.customData?.configOverrides, setMembershipOverrides]);

  useEffect(() => {
    if (!membership?.id) {
      // No sense to remove context from Sentry and GA
      // If customer logs out and log in under other account - we are covered
      return;
    }
    sentrySetUser({
      id: membership.customerId,
      membership_id: membership.id,
      profile_completed: membership.isCompleted,
      activation_state: membership.state,
    });
    setCustomerUserId({ cuid: membership.customerId });
  }, [membership?.customerId, membership?.id, membership?.isCompleted, membership?.state, setCustomerUserId]);

  useEffect(() => {
    if (!membership?.customerId) {
      return;
    }

    identify(membership.customerId, {
      customerUuid: membership.customerId,
      membershipId: membership.id,
      points: membership.points,
      memberState: membership.state,
      platform: Capacitor.getPlatform(),
      // TODO: value is here. We just need to refactor config context
      loyaltyBrand: config?.retailer || '',
      homeStore: membership.homeStores.length > 0 ? membership.homeStores[0] : '',
    });
  }, [
    membership?.customerId,
    membership?.id,
    membership?.points,
    membership?.state,
    membership?.homeStores,
    identify,
    config?.retailer,
  ]);

  const [activateCustomer, { loading: isCustomerActivating }] = useCustomerMakeActiveMutation({
    refetchQueries: ['offers'],
    onCompleted: () => {
      logAppsFlyerEvent({
        // It was decided to hardcode the event name for Gulf until AppsFlyer
        // will be used by other clients. It should be changed to more generic name later.
        eventName: 'gp_phone_number_entered',
      });
    },
  });
  useEffect(() => {
    if (membership?.state === MEMBERSHIP_STATE.NewMember) {
      activateCustomer().catch(logInDev);
    }
  }, [activateCustomer, membership?.state]);

  // Remove cached (in memory) wallet pin code if user does not have saved pin code
  // examples: all payment method removed, some internal issue and pin code did reset
  const { eraseStorageValue: eraseCachedPinCode, value: cachedPinCode } =
    useInMemoryStorage<string>(IM_MEMORY_PIN_CODE_STORAGE_KEY);
  useEffect(() => {
    if (!membership?.hasPasscode && cachedPinCode) {
      eraseCachedPinCode();
    }
  }, [cachedPinCode, eraseCachedPinCode, membership?.hasPasscode]);

  return useMemo(
    () => ({
      loading: isMembershipLoading || isCustomerActivating,
      membership,
      refetch,
    }),
    [isCustomerActivating, isMembershipLoading, membership, refetch],
  );
};
